<template>
  <div class="establishments-variation-section">
    <h3 class="section-title">
      <i class="fas fa-exchange-alt"></i> Estabelecimentos com Maior Variação
    </h3>
    
    <div class="variation-grid">
      <!-- Maiores melhorias -->
      <div class="variation-column positive">
        <h4 class="variation-column-title">
          <i class="fas fa-arrow-up"></i> Maiores Melhorias
        </h4>
        <div class="establishments-list" v-if="topImprovements.length > 0">
          <div class="establishment-card" v-for="(item, index) in topImprovements" :key="'improvement-'+index">
            <div class="establishment-info">
              <div class="establishment-name">{{ item.name }}</div>
              <div class="establishment-details">
                <span class="category-label">{{ item.category }}</span>
                <span class="address">{{ truncateText(item.address, 40) }}</span>
              </div>
            </div>
            <div class="rating-change positive">
              <div class="current-rating">{{ item.currentRating.toFixed(1) }}</div>
              <div class="rating-variation">+{{ item.variation.toFixed(1) }}</div>
            </div>
          </div>
        </div>
        <div class="no-data" v-else>
          <i class="fas fa-info-circle"></i>
          <span>Não há dados de melhoria para este período</span>
        </div>
      </div>
      
      <!-- Maiores quedas -->
      <div class="variation-column negative">
        <h4 class="variation-column-title">
          <i class="fas fa-arrow-down"></i> Maiores Quedas
        </h4>
        <div class="establishments-list" v-if="topDeclines.length > 0">
          <div class="establishment-card" v-for="(item, index) in topDeclines" :key="'decline-'+index">
            <div class="establishment-info">
              <div class="establishment-name">{{ item.name }}</div>
              <div class="establishment-details">
                <span class="category-label">{{ item.category }}</span>
                <span class="address">{{ truncateText(item.address, 40) }}</span>
              </div>
            </div>
            <div class="rating-change negative">
              <div class="current-rating">{{ item.currentRating.toFixed(1) }}</div>
              <div class="rating-variation">{{ item.variation.toFixed(1) }}</div>
            </div>
          </div>
        </div>
        <div class="no-data" v-else>
          <i class="fas fa-info-circle"></i>
          <span>Não há dados de queda para este período</span>
        </div>
      </div>
    </div>
    
    <div class="variation-chart">
      <h4 class="chart-title">Distribuição de Variações</h4>
      <div class="chart-container">
        <canvas id="variationChart" height="250"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  props: {
    categoryData: Object,
    month: Number,
    year: Number
  },
  data() {
    return {
      variationChart: null,
      topImprovements: [],
      topDeclines: []
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.analyzeEstablishmentsVariation()
      this.createVariationChart()
    })
  },
  beforeDestroy() {
    if (this.variationChart) {
      this.variationChart.destroy()
      this.variationChart = null
    }
  },
  methods: {
    analyzeEstablishmentsVariation() {
      if (!this.categoryData || !this.categoryData.establishments) {
        return
      }
      
      const currentMonth = this.month
      const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1
      const previousYear = currentMonth === 1 ? this.year - 1 : this.year
      
      const variationData = []
      
      this.categoryData.establishments.forEach(establishment => {
        if (!establishment.ratings) return
        
        const currentMonthRating = this.getEstablishmentMonthlyRating(establishment, this.year, currentMonth)
        const previousMonthRating = this.getEstablishmentMonthlyRating(establishment, previousYear, previousMonth)
        
        if (currentMonthRating === 0 || previousMonthRating === 0) return
        
        const variation = currentMonthRating - previousMonthRating
        
        variationData.push({
          name: establishment.name,
          category: establishment.subcategory || 'N/A',
          address: establishment.full_address || 'Endereço não disponível',
          currentRating: currentMonthRating,
          previousRating: previousMonthRating,
          variation: variation
        })
      })
      
      // Ordenar por variação (do maior para o menor)
      variationData.sort((a, b) => b.variation - a.variation)
      
      // Pegar os 5 melhores e 5 piores
      this.topImprovements = variationData.filter(item => item.variation > 0).slice(0, 5)
      this.topDeclines = variationData.filter(item => item.variation < 0).slice(-5).reverse()
    },
    
    getEstablishmentMonthlyRating(establishment, year, month) {
      if (!establishment.ratings || !establishment.ratings[year] || !establishment.ratings[year][month]) {
        return 0
      }
      
      return establishment.ratings[year][month].average_rating || 0
    },
    
    createVariationChart() {
      const canvas = document.getElementById('variationChart')
      if (!canvas) return
      
      // Destruir gráfico anterior se existir
      if (this.variationChart) {
        this.variationChart.destroy()
        this.variationChart = null
      }
      
      const ctx = canvas.getContext('2d')
      
      // Preparar dados para o gráfico de barras
      const variations = this.getVariationsDistribution()
      
      const labels = Object.keys(variations).map(key => {
        if (key === 'major_decline') return 'Queda Forte (< -1.0)'
        if (key === 'moderate_decline') return 'Queda Moderada (-0.5 a -1.0)'
        if (key === 'slight_decline') return 'Queda Leve (0 a -0.5)'
        if (key === 'stable') return 'Estável (0)'
        if (key === 'slight_improvement') return 'Melhora Leve (0 a 0.5)'
        if (key === 'moderate_improvement') return 'Melhora Moderada (0.5 a 1.0)'
        return 'Melhora Forte (> 1.0)'
      })
      
      const data = Object.values(variations)
      const colors = [
        '#ef4444', // vermelho forte
        '#f87171', // vermelho moderado
        '#fca5a5', // vermelho leve
        '#94a3b8', // neutro
        '#86efac', // verde leve
        '#4ade80', // verde moderado
        '#22c55e'  // verde forte
      ]
      
      this.variationChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Quantidade de Estabelecimentos',
            data: data,
            backgroundColor: colors,
            borderColor: colors.map(color => color),
            borderWidth: 1,
            borderRadius: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.parsed.y} estabelecimentos`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                precision: 0
              },
              title: {
                display: true,
                text: 'Número de Estabelecimentos',
                font: {
                  size: 13,
                  weight: 'bold'
                }
              }
            },
            x: {
              ticks: {
                font: {
                  size: 11
                }
              }
            }
          }
        }
      })
    },
    
    getVariationsDistribution() {
      if (!this.categoryData || !this.categoryData.establishments) {
        return {
          major_decline: 0,
          moderate_decline: 0,
          slight_decline: 0,
          stable: 0,
          slight_improvement: 0,
          moderate_improvement: 0,
          major_improvement: 0
        }
      }
      
      const currentMonth = this.month
      const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1
      const previousYear = currentMonth === 1 ? this.year - 1 : this.year
      
      const distribution = {
        major_decline: 0,
        moderate_decline: 0,
        slight_decline: 0,
        stable: 0,
        slight_improvement: 0,
        moderate_improvement: 0,
        major_improvement: 0
      }
      
      this.categoryData.establishments.forEach(establishment => {
        if (!establishment.ratings) return
        
        const currentMonthRating = this.getEstablishmentMonthlyRating(establishment, this.year, currentMonth)
        const previousMonthRating = this.getEstablishmentMonthlyRating(establishment, previousYear, previousMonth)
        
        if (currentMonthRating === 0 || previousMonthRating === 0) return
        
        const variation = currentMonthRating - previousMonthRating
        
        if (variation < -1.0) distribution.major_decline++
        else if (variation >= -1.0 && variation < -0.5) distribution.moderate_decline++
        else if (variation >= -0.5 && variation < 0) distribution.slight_decline++
        else if (variation === 0) distribution.stable++
        else if (variation > 0 && variation <= 0.5) distribution.slight_improvement++
        else if (variation > 0.5 && variation <= 1.0) distribution.moderate_improvement++
        else distribution.major_improvement++
      })
      
      return distribution
    },
    
    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    }
  }
}
</script>

<style scoped>
.establishments-variation-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 20px;
  color: #0f172a;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.variation-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
  margin-bottom: 30px;
}

.variation-column {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.variation-column.positive {
  border-top: 4px solid #22c55e;
}

.variation-column.negative {
  border-top: 4px solid #ef4444;
}

.variation-column-title {
  font-size: 16px;
  font-weight: 600;
  padding: 15px 20px;
  margin: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 8px;
}

.positive .variation-column-title {
  color: #166534;
}

.positive .variation-column-title i {
  color: #22c55e;
}

.negative .variation-column-title {
  color: #991b1b;
}

.negative .variation-column-title i {
  color: #ef4444;
}

.establishments-list {
  padding: 15px;
  max-height: 400px;
  overflow-y: auto;
}

.establishment-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.2s;
}

.establishment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.establishment-card:last-child {
  margin-bottom: 0;
}

.establishment-info {
  flex: 1;
}

.establishment-name {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 5px;
}

.establishment-details {
  display: flex;
  gap: 10px;
  font-size: 13px;
  color: #64748b;
  flex-wrap: wrap;
}

.category-label {
  background: #e2e8f0;
  color: #475569;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.rating-change {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 70px;
}

.current-rating {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.rating-variation {
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
}

.positive .rating-variation {
  background: #dcfce7;
  color: #166534;
}

.negative .rating-variation {
  background: #fee2e2;
  color: #991b1b;
}

.no-data {
  padding: 30px 20px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.no-data i {
  font-size: 24px;
  color: #94a3b8;
}

.variation-chart {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 15px;
  color: #0f172a;
}

.chart-container {
  height: 250px;
  position: relative;
}

@media (max-width: 992px) {
  .variation-grid {
    grid-template-columns: 1fr;
  }
}
</style> 